export type SharePreferences = {
    includeEmail: boolean;
    includePhone: boolean;
    includeCompanyTitle: boolean;
    includeLinkedIn: boolean;
    /**
     * Optional full message template that overrides the default intro/header logic.
     * Supported placeholders:
     * {name}     -> Recipient contact name (first + last trimmed)
     * {my_name}  -> Your profile display name (display_name or first + last)
     * {location} -> Meeting/location label if available
     * Placeholders that cannot be resolved are removed (collapsed to single spaces, blank lines trimmed).
     */
    template?: string;
};

export const defaultSharePreferences: SharePreferences = {
    includeEmail: true,
    includePhone: true,
    includeCompanyTitle: true,
    includeLinkedIn: true,
    // Single unified sharing template; supports {name} and {location}
    template: "Hey {name}. It's {my_name}. It was great meeting you at {location}. We should hang out sometime.",
};

const GLOBAL_LS_KEY = 'gv:sharePrefs';

function keyForVault(groupId?: string | null) {
    const gid = (groupId || '').trim();
    return gid ? `gv:sharePrefs:${gid}` : GLOBAL_LS_KEY;
}

export function loadSharePreferences(groupId?: string | null): SharePreferences {
    try {
        // Prefer per-vault key; migrate from global if needed
        const lsKey = keyForVault(groupId);
        let raw = localStorage.getItem(lsKey);
        if (!raw && groupId) {
            const legacy = localStorage.getItem(GLOBAL_LS_KEY);
            if (legacy) {
                raw = legacy;
                // Do not delete the legacy key automatically; just use as seed for this vault
                try { localStorage.setItem(lsKey, legacy); } catch { }
            }
        }
        if (!raw) return { ...defaultSharePreferences };
        const parsed = JSON.parse(raw) as any;
        let template = typeof parsed?.template === 'string' ? parsed.template : defaultSharePreferences.template;
        // Strip legacy {details} only (still support {my_name})
        template = template.replace(/\{details\}/gi, '');
        return {
            includeEmail: parsed?.includeEmail ?? defaultSharePreferences.includeEmail,
            includePhone: parsed?.includePhone ?? defaultSharePreferences.includePhone,
            includeCompanyTitle: parsed?.includeCompanyTitle ?? defaultSharePreferences.includeCompanyTitle,
            includeLinkedIn: parsed?.includeLinkedIn ?? defaultSharePreferences.includeLinkedIn,
            template,
        };
    } catch {
        return { ...defaultSharePreferences };
    }
}

export function saveSharePreferences(prefs: SharePreferences, groupId?: string | null) {
    try {
        localStorage.setItem(keyForVault(groupId), JSON.stringify(prefs));
    } catch {
        // ignore
    }
}
